import React, { useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Dropdown } from 'react-bootstrap'

import { isExcelFile, saveAs } from 'helpers'
import downloadMonitoringTemplate from 'api/imports/downloadTemplate'
import * as fromActions from 'store/actions/monitoring.actions'
import { useTranslationRankmi } from 'helpers/translations.helper'
import UploadFileV1 from 'components/ui/modals/UploadFile/V1'
import { IMainState } from 'store/states'

interface UploadButtonProps {
  className?: string
  refetchCourseUsers?: () => void
  disabled?: boolean
}

const UploadButton: React.FC<UploadButtonProps> = ({
  className,
  refetchCourseUsers,
  disabled,
}) => {
  const [error, setError] = React.useState<React.ReactNode>(null)
  const { t } = useTranslation()
  const tUpload = useTranslationRankmi({
    prefix: 'administration.monitor.upload',
  })
  const dispatch = useDispatch()
  const course = useSelector(({ courses }: IMainState) => courses.course)

  const [show, setShow] = useState(false)
  const handleClose = () => {
    setShow(false)
    setError(null)
  }

  const handleShow = () => setShow(true)

  const handleFileUpload = (file: File) => {
    setError(null)
    if (!isExcelFile(file)) {
      setTimeout(() => {
        setError(<>{tUpload('modal.error')}</>)
      }, 500)
      return
    }
    if (course.id)
      dispatch(
        fromActions.Actions.uploadFile({
          id: course.id,
          file,
          onSuccess: refetchCourseUsers,
        })
      )
    setShow(false)
  }

  const isSynchronousCourse = !!course.synchronousCourse?.id

  const template = useMemo(() => {
    if (isSynchronousCourse) {
      return {
        link: 'https://rankmi-lms-prod.s3.amazonaws.com/templates/participants.xlsx',
        label: tUpload('modal.template'),
      }
    }

    return {
      label: tUpload('modal.template'),
      onDownload: async () => {
        const { data } = await downloadMonitoringTemplate(
          'import_participants',
          course.id
        )

        saveAs(data, `result_template_${course.id}.xlsx`)
      },
    }
  }, [isSynchronousCourse, tUpload])

  return (
    <>
      <Dropdown.Item
        className={className}
        onClick={handleShow}
        disabled={disabled}
      >
        {tUpload('labelShort')}
      </Dropdown.Item>
      <UploadFileV1
        show={show}
        title={tUpload('modal.title')}
        labels={{
          legend: tUpload('modal.legend'),
          addFile: tUpload('modal.addFile'),
          close: t('commons.buttons.close'),
        }}
        template={template}
        onClose={() => {
          handleClose()
        }}
        onUpload={handleFileUpload}
        error={error}
      />
    </>
  )
}

export default UploadButton
